import { Flex, Text } from '@chakra-ui/react';
import {
  ButtonText,
  Checkbox,
  FormFeedback,
  Input,
  OutlineCard
} from '@morressier/alembic';
import { get } from 'lodash';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { Field, FieldLabel } from 'src/components/Field';
import { RadioGroup } from 'src/components/RadioGroup';
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FormFieldSingleFileUpload } from 'src/components/FormField';
import { FormFieldFiguresAndTables } from '@publishing-engine/domain/types/article';
import { IFigureValue } from './FiguresAndTablesUpdateModal';
import { getFileFieldConfig } from '../File';

interface Props {
  config: Omit<FormFieldFiguresAndTables, 'component'>;
  methods: UseFormReturn<IFigureValue>;
  submissionContextId: string;
  formContext?: 'create' | 'edit';
}

const FiguresAndTablesForm: React.FC<Props> = ({
  config,
  methods,
  submissionContextId,
  formContext = 'edit'
}) => {
  const {
    control,
    formState: { errors },
    watch,
    resetField,
    register,
    clearErrors
  } = methods;
  const {
    params: { validations, labels },
    caseDataFields: { main: field, dependsOn }
  } = config;

  const dependsOnField = `${field}.${dependsOn}` as const;
  const mainField = `${field}.value` as const;

  const figuresAndTables = watch(mainField);
  const hasFiguresAndTables = watch(dependsOnField);
  const mainError = get(errors, mainField);
  const dependsOnError = get(errors, dependsOnField);

  const { fields, append, remove } = useFieldArray({
    control,
    name: mainField
  });

  const addPermissionRow = () => {
    clearErrors(mainField);
    append({ referenceName: '', isReproduced: false });
  };

  const removePermissionRow = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    resetField(mainField);
    if (hasFiguresAndTables && !fields.length) {
      addPermissionRow();
    }
  }, [hasFiguresAndTables]);

  return (
    <Flex flexDirection="column" gap={4} width="100%">
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingSection">
          Update {config.params.labels.main}
        </Text>
      </Flex>

      <OutlineCard
        background={
          formContext === 'edit' ? 'surfaceSecondaryFill' : 'surfacePrimaryFill'
        }
        borderless
        width="100%"
        padding={8}
      >
        <Flex flexDirection="column" gap={4}>
          <FieldLabel
            isRequired={config.params.validations.required}
            label={config.params.labels.section.title}
            subText=""
            helpText={labels.helpText}
          />

          <RadioGroup
            name={dependsOnField}
            control={control}
            options={[
              {
                value: true,
                children: 'Yes'
              },
              {
                value: false,
                children: 'No'
              }
            ]}
          />

          {dependsOnError ? (
            <FormFeedback variant="error">
              Please indicate whether your submission includes figures and
              tables to proceed
            </FormFeedback>
          ) : null}
        </Flex>
      </OutlineCard>

      {hasFiguresAndTables ? (
        <Flex flexDirection="column" gap={4}>
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
          />

          <Flex gap={4} flexDirection="column" width="100%">
            {fields.map((field, index) => (
              <OutlineCard
                background={
                  formContext === 'edit'
                    ? 'surfaceSecondaryFill'
                    : 'surfacePrimaryFill'
                }
                borderless
                width="100%"
                padding={8}
                key={field.id}
              >
                <Flex flex={14} gap={4} flexDirection={'column'}>
                  <Field isRequired label="Figure">
                    <Input
                      {...register(`${mainField}.${index}.referenceName`)}
                      isInvalid={!!mainError?.[index]?.referenceName}
                      placeholder="e.g., Figure 1.1"
                    />
                    {mainError?.[index]?.referenceName && (
                      <FormFeedback variant="error">
                        Please provide a valid name for this work.
                      </FormFeedback>
                    )}
                  </Field>

                  {config.params.isSourceFileEnabled && (
                    <Flex flexDirection={'column'} gap={2}>
                      <FormFieldSingleFileUpload
                        submissionContextId={submissionContextId}
                        methods={methods}
                        config={getFileFieldConfig({
                          name: 'Source File',
                          fieldKey: `${mainField}.${index}.sourceFile`,
                          validations: config.params.validations.sourceFile
                        })}
                      />
                    </Flex>
                  )}

                  <Checkbox
                    id={`${mainField}.${index}.isReproduced`}
                    {...register(`${mainField}.${index}.isReproduced`)}
                    ariaLabel="Tick if this is a reproduced work?"
                  >
                    This is a reproduced work
                  </Checkbox>

                  {config.params.isPermissionEvidenceEnabled &&
                    figuresAndTables?.[index]?.isReproduced && (
                      <FormFieldSingleFileUpload
                        methods={methods}
                        submissionContextId={submissionContextId}
                        config={getFileFieldConfig({
                          name: 'Permission Evidence',
                          fieldKey: `${mainField}.${index}.permissionEvidence`,
                          validations:
                            config.params.validations.permissionEvidence
                        })}
                      />
                    )}

                  <ButtonText
                    alignSelf="baseline"
                    onClick={() => removePermissionRow(index)}
                    iconLeft={<FontAwesomeIcon icon={faTrash} />}
                  >
                    Remove
                  </ButtonText>
                </Flex>
              </OutlineCard>
            ))}
          </Flex>

          {mainError?.['root'] ? (
            <FormFeedback variant="error">
              Please provide figures and tables data
            </FormFeedback>
          ) : null}

          <ButtonText
            alignSelf="baseline"
            iconLeft={<FontAwesomeIcon icon={faPlus} />}
            onClick={addPermissionRow}
          >
            Add Figure
          </ButtonText>
        </Flex>
      ) : null}
    </Flex>
  );
};

export default FiguresAndTablesForm;
